<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher, onMount } from 'svelte';
  import * as apiService from '../lib/apiService';
  import { actionFlags, actionFlagHelpers } from '../stores/actionFlags';
  import { showToast } from '../lib/toast';

  const dispatch = createEventDispatcher();
  // @ts-ignore - Vite-specific type
  const BUILD_TIMESTAMP = import.meta.env.VITE_BUILD_TIMESTAMP || Date.now();

  type Logo = { filename: string, url: string, key_path: string };
  
  export let currentLogo: Logo = { filename: 'no logo', url: '/img/clublogos/default.png?v=${BUILD_TIMESTAMP}', key_path: 'clublogos/default.png' };
  export let isOpen: boolean = false;

  let mode: 'select' | 'upload' = 'select';
  let availableLogos: Logo[] = [];
  let selectedLogo: Logo = currentLogo;
  let searchText = '';
  let fileInput: HTMLInputElement;
  let selectedFile: File | null = null;
  let previewUrl: string = '';

  const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  // Only load logos when user searches with 3+ characters
  $: if (isOpen && mode === 'select' && searchText.length >= 3) {
    loadLogos();
  }

  // Filter logos based on search
  let filteredLogos: Logo[] = [];
  $: filteredLogos = searchText.length >= 3
    ? availableLogos.filter((logo: Logo) =>
        logo.filename.toLowerCase().includes(searchText.toLowerCase())
      )
    : [];

  async function loadLogos() {
    if ($actionFlags.isLoadingLogos) return;

    actionFlagHelpers.setFlag('isLoadingLogos');
    try {
      await apiService.getClubLogos((result, status) => {
        if (status === 200) {
          availableLogos = result;
        } else {
          showToast($_('clubSettings.errors.loadLogos'), 'error');
        }
      });
    } catch (error) {
      console.error('Error loading logos:', error);
      showToast($_('clubSettings.errors.loadLogos'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isLoadingLogos');
    }
  }

  function selectLogo(logo: Logo) {
    selectedLogo = logo;
  }
  
  // Helper to ensure type inference in templates
  function getLogo(logo: unknown): Logo {
    return logo as Logo;
  }

  function confirmSelection() {
    if (mode === 'select' && selectedLogo) {
      dispatch('selectLogo', { filename: selectedLogo.filename, url: selectedLogo.url, key_path: selectedLogo.key_path });
      closeModal();
    } else if (mode === 'upload' && selectedFile) {
      uploadLogo();
    }
  }

  function handleFileSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) return;

    // Validate file extension
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      showToast(
        $_('clubSettings.errors.invalidFileType', {
          values: { types: ALLOWED_EXTENSIONS.join(', ') }
        }),
        'error'
      );
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      showToast(
        $_('clubSettings.errors.fileTooLarge', {
          values: { size: '5MB' }
        }),
        'error'
      );
      return;
    }

    selectedFile = file;

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      previewUrl = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  async function uploadLogo() {
    if (!selectedFile || $actionFlags.isUploadingLogo) return;

    actionFlagHelpers.setFlag('isUploadingLogo');
    try {
      await apiService.uploadClubLogo(selectedFile, (result, status) => {
        if (status === 200) {
          showToast($_('clubSettings.success.logoUploaded'), 'success');
          dispatch('uploadLogo', { filename: result.filename, url: result.url, key_path: result.key_path });
          closeModal();
        } else {
          showToast($_('clubSettings.errors.uploadFailed'), 'error');
        }
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      showToast($_('clubSettings.errors.uploadFailed'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isUploadingLogo');
    }
  }

  function clearFileSelection() {
    selectedFile = null;
    previewUrl = '';
    if (fileInput) {
      fileInput.value = '';
    }
  }

  function closeModal() {
    dispatch('close');
  }

  function switchMode(newMode: 'select' | 'upload') {
    mode = newMode;
    if (newMode === 'select') {
      clearFileSelection();
      // Don't preload logos - wait for user to search
    } else {
      searchText = '';
    }
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="logo-selector-overlay" on:click={closeModal}>
    <div class="logo-selector-modal" on:click|stopPropagation>
      <header>
        <h4>{$_('clubSettings.logoSelector.title')}</h4>
        <button class="close-button" on:click={closeModal}>×</button>
      </header>

      <!-- Mode Toggle -->
      <div class="mode-toggle">
        <button
          class="tab-button {mode === 'select' ? 'active' : ''}"
          on:click={() => switchMode('select')}
        >
          <ion-icon name="images-outline"></ion-icon>
          {$_('clubSettings.logoSelector.selectExisting')}
        </button>
        <button
          class="tab-button {mode === 'upload' ? 'active' : ''}"
          on:click={() => switchMode('upload')}
        >
          <ion-icon name="cloud-upload-outline"></ion-icon>
          {$_('clubSettings.logoSelector.uploadNew')}
        </button>
      </div>

      <div class="modal-content">
        {#if mode === 'select'}
          <!-- Select Existing Logo Mode -->
          <div class="search-container">
            <input
              type="text"
              class="search-input"
              placeholder={$_('clubSettings.logoSelector.searchPlaceholder')}
              bind:value={searchText}
            />
          </div>

          <div class="logos-grid">
            {#if searchText.length < 3}
              <div class="empty-state">
                <ion-icon name="search-outline" class="empty-icon"></ion-icon>
                <p>{$_('clubSettings.logoSelector.searchPrompt')}</p>
              </div>
            {:else if $actionFlags.isLoadingLogos}
              <div class="loading-state">
                <p>{$_('clubSettings.logoSelector.loading')}</p>
              </div>
            {:else if filteredLogos.length === 0}
              <div class="empty-state">
                <p>{$_('clubSettings.logoSelector.noLogos')}</p>
              </div>
            {:else}
              {#each filteredLogos as logo, i (i)}
                {@const l = getLogo(logo)}
                <button
                  type="button"
                  class="logo-card {selectedLogo.filename === l.filename ? 'selected' : ''}"
                  on:click={() => selectLogo(l)}
                >
                  <img
                    src={l.url}
                    alt={l.filename}
                    class="logo-preview"
                  />
                  <span class="logo-name">{l.filename}</span>
                </button>
              {/each}
            {/if}
          </div>
        {:else}
          <!-- Upload New Logo Mode -->
          <div class="upload-container">
            <div class="upload-area">
              <input
                type="file"
                bind:this={fileInput}
                on:change={handleFileSelect}
                accept=".png,.jpg,.jpeg,.webp"
                class="file-input"
                id="logoFileInput"
              />
              <label for="logoFileInput" class="upload-label">
                {#if previewUrl}
                  <img src={previewUrl} alt="Preview" class="upload-preview" />
                  <p class="file-name">{selectedFile?.name}</p>
                  <button
                    type="button"
                    class="small outline"
                    on:click|preventDefault={clearFileSelection}
                  >
                    {$_('clubSettings.logoSelector.changeFile')}
                  </button>
                {:else}
                  <ion-icon name="cloud-upload-outline" class="upload-icon"></ion-icon>
                  <p>{$_('clubSettings.logoSelector.dragDrop')}</p>
                  <p class="text-sm text-muted">
                    {$_('clubSettings.logoSelector.fileRequirements')}
                  </p>
                {/if}
              </label>
            </div>
          </div>
        {/if}
      </div>

      <footer>
        <button
          type="button"
          class="secondary outline"
          on:click={closeModal}
        >
          {$_('common.cancel')}
        </button>
        <button
          type="button"
          on:click={confirmSelection}
          disabled={(mode === 'select' && !selectedLogo) || (mode === 'upload' && !selectedFile) || $actionFlags.isUploadingLogo}
        >
          {#if mode === 'upload' && $actionFlags.isUploadingLogo}
            {$_('clubSettings.logoSelector.uploading')}
          {:else}
            {$_('common.confirm')}
          {/if}
        </button>
      </footer>
    </div>
  </div>
{/if}

<style>
  .logo-selector-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    backdrop-filter: blur(4px);
  }

  .logo-selector-modal {
    background-color: var(--pico-background-color);
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    border-radius: var(--pico-border-radius);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 1px solid var(--pico-muted-border-color);
  }

  header h4 {
    margin: 0;
  }

  .close-button {
    background: transparent;
    border: none;
    color: var(--pico-color);
    font-size: 2rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
  }

  .mode-toggle {
    display: flex;
    gap: 0;
    padding: 1rem 1.5rem 0;
    border-bottom: 1px solid var(--pico-muted-border-color);
  }

  .tab-button {
    flex: 1;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    color: var(--pico-muted-color);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.2s;
  }

  .tab-button:hover {
    color: var(--pico-color);
    background-color: var(--pico-secondary-hover-background);
  }

  .tab-button.active {
    color: var(--pico-primary);
    border-bottom-color: var(--pico-primary);
  }

  .modal-content {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
  }

  .search-container {
    margin-bottom: 1.5rem;
  }

  .search-input {
    width: 100%;
  }

  .logos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
  }

  .logo-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1rem;
    background-color: var(--pico-card-background-color);
    border: 2px solid var(--pico-muted-border-color);
    border-radius: var(--pico-border-radius);
    cursor: pointer;
    transition: all 0.2s;
  }

  .logo-card:hover {
    border-color: var(--pico-primary);
    transform: translateY(-2px);
  }

  .logo-card.selected {
    border-color: var(--pico-primary);
    background-color: var(--pico-primary-hover);
  }

  .logo-preview {
    width: 80px;
    height: 80px;
    object-fit: contain;
    margin-bottom: 0.5rem;
  }

  .logo-name {
    font-size: 0.75rem;
    text-align: center;
    word-break: break-all;
    color: var(--pico-muted-color);
  }

  .upload-container {
    display: flex;
    justify-content: center;
    padding: 2rem 0;
  }

  .upload-area {
    width: 100%;
    max-width: 500px;
  }

  .file-input {
    display: none;
  }

  .upload-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    padding: 2rem;
    border: 2px dashed var(--pico-muted-border-color);
    border-radius: var(--pico-border-radius);
    cursor: pointer;
    transition: all 0.2s;
    text-align: center;
  }

  .upload-label:hover {
    border-color: var(--pico-primary);
    background-color: var(--pico-secondary-hover-background);
  }

  .upload-icon {
    font-size: 4rem;
    color: var(--pico-muted-color);
    margin-bottom: 1rem;
  }

  .upload-preview {
    max-width: 200px;
    max-height: 200px;
    object-fit: contain;
    margin-bottom: 1rem;
  }

  .file-name {
    margin: 0.5rem 0;
    font-weight: bold;
  }

  .loading-state,
  .empty-state {
    text-align: center;
    padding: 3rem;
    color: var(--pico-muted-color);
  }

  .empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 1.5rem;
    border-top: 1px solid var(--pico-muted-border-color);
  }
</style>
